import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useRecordPaymentMutation,
  usePatchPaymentMutation,
  useRemovePaymentMutation,
  useAttachContributionToPaymentMutation,
  useDetachContributionMutation,
  useEditStudentWithAgreementMutation,
  ContributionInput,
  FakturowniaInvoicesDocument,
  StudentDetailDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IStudentLike {
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    middleName?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
}

interface IAgreementLike {
  id: string;
  agreementTitle: string;
  currency?: string | null;
  budgetAmount?: number | null;
  group: { id: string };
  date?: string | null;
  comment?: string | null;
  paymentByCompany?: boolean | null;
  vat?: boolean | null;
  resident?: boolean | null;
  terminated?: boolean | null;
  terminatedDate?: string | null;
  partialAttendance?: boolean | null;
  promocode?: string | null;
  country?: { id: string } | null;
  company?: { id: string } | null;
  contributions: {
    edges: Array<{
      node?: { id: string; date: string; amountForeign?: number | null } | null;
    } | null>;
  };
}

interface ICreateArgs {
  agreementId: string;
  amountAgreement: number;
  date: string;
  currency: string;
  /** Phase 13 §9 — when present, backend atomically links the new
   *  payment to the given scheduled installment. */
  contributionId?: string;
}

interface IUpdateArgs {
  paymentId: string;
  amountAgreement?: number;
  date?: string;
}

interface IAddContributionArgs {
  student: IStudentLike;
  agreement: IAgreementLike;
  date: string;
  amountForeign: number;
}

interface IPaymentMutationsHook {
  createPayment: (args: ICreateArgs) => Promise<boolean>;
  updatePayment: (args: IUpdateArgs) => Promise<boolean>;
  deletePayment: (paymentId: string) => Promise<boolean>;
  linkContribution: (
    contributionId: string,
    paymentId: string,
  ) => Promise<boolean>;
  /**
   * @deprecated Used once-historically to detach a Payment from a
   *   Contribution (leaving the Payment orphan). The new schedule-first
   *   UI doesn't take that route anymore — paid contributions are reset
   *   by deleting the Payment outright. Kept for admin/legacy callers.
   */
  unlinkContribution: (contributionId: string) => Promise<boolean>;
  /**
   * Adds a single Contribution to the agreement's schedule. Used by the
   * inline «+ Взнос в план» action on PaymentsBlock when the agreement
   * has no PandaDoc-driven schedule (or the operator needs an extra
   * planned row). Internally uses `editStudentWithAgreement` with the
   * full existing schedule + the new row appended — the mutation does
   * not support a single-row patch (see backend §10.1 candidate).
   */
  addContribution: (args: IAddContributionArgs) => Promise<boolean>;
  loading: boolean;
}

/**
 * Phase 13 follow-up bundle: free-standing `createPayment` /
 * `updatePayment` / `deletePayment` mutations behind one hook for the
 * student card. Each call refetches `StudentDetail` (so the card's KPI
 * strip and AgreementCard progress bar repaint) and
 * `fakturowniaInvoices` (so the orphan-invoices block reflects the
 * fresh unlinked/linked state).
 *
 * Mirrors the toast/error vocabulary of `useInvoiceMatching` — same
 * `GlobalToastTypeNames` channel, same `customErrors` pattern.
 */
export const usePaymentMutations = (
  studentId?: string,
): IPaymentMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const refetchQueries = useMemo(() => {
    const queries: Array<
      | { query: typeof StudentDetailDocument; variables?: { id: string } }
      | { query: typeof FakturowniaInvoicesDocument }
    > = [{ query: FakturowniaInvoicesDocument }];
    if (studentId) {
      queries.push({
        query: StudentDetailDocument,
        variables: { id: studentId },
      });
    }
    return queries;
  }, [studentId]);

  const [createMutation, createState] = useRecordPaymentMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [updateMutation, updateState] = usePatchPaymentMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [deleteMutation, deleteState] = useRemovePaymentMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [attachMutation, attachState] = useAttachContributionToPaymentMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [detachMutation, detachState] = useDetachContributionMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [
    editAgreementMutation,
    editAgreementState,
  ] = useEditStudentWithAgreementMutation({
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const notifySuccess = useCallback(
    (title: string) => {
      addToast({
        data: { type: ToastTypes.success, title },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const notifyError = useCallback(
    (
      title: string,
      errors?: Array<{
        message?: string | null;
        code?: string | null;
      } | null> | null,
    ) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title,
          text:
            errors
              ?.map((e) => e?.message || e?.code || null)
              .filter(Boolean)
              .join('\n') || undefined,
        },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast],
  );

  const createPayment = useCallback(
    async (args: ICreateArgs) => {
      try {
        const result = await createMutation({
          variables: { input: args },
        });
        const payload = result.data?.createPayment;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.payments.toasts.created'));
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.createFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.studentDetail.payments.toasts.createFailed'), [
          { message: (error as Error).message, code: null },
        ]);
        return false;
      }
    },
    [createMutation, notifySuccess, notifyError, t],
  );

  const updatePayment = useCallback(
    async ({ paymentId, ...rest }: IUpdateArgs) => {
      try {
        const result = await updateMutation({
          variables: { input: { id: paymentId, ...rest } },
        });
        const payload = result.data?.updatePayment;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.payments.toasts.updated'));
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.updateFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.studentDetail.payments.toasts.updateFailed'), [
          { message: (error as Error).message, code: null },
        ]);
        return false;
      }
    },
    [updateMutation, notifySuccess, notifyError, t],
  );

  const deletePayment = useCallback(
    async (paymentId: string) => {
      try {
        const result = await deleteMutation({
          variables: { id: paymentId },
        });
        const payload = result.data?.deletePayment;
        if (payload?.success) {
          notifySuccess(t('pages.studentDetail.payments.toasts.deleted'));
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.deleteFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(t('pages.studentDetail.payments.toasts.deleteFailed'), [
          { message: (error as Error).message, code: null },
        ]);
        return false;
      }
    },
    [deleteMutation, notifySuccess, notifyError, t],
  );

  const linkContribution = useCallback(
    async (contributionId: string, paymentId: string) => {
      try {
        const result = await attachMutation({
          variables: { input: { contributionId, paymentId } },
        });
        const payload = result.data?.linkContributionToPayment;
        if (payload?.success) {
          notifySuccess(
            t('pages.studentDetail.payments.toasts.contributionLinked'),
          );
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionLinkFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionLinkFailed'),
          [{ message: (error as Error).message, code: null }],
        );
        return false;
      }
    },
    [attachMutation, notifySuccess, notifyError, t],
  );

  const unlinkContribution = useCallback(
    async (contributionId: string) => {
      try {
        const result = await detachMutation({
          variables: { contributionId },
        });
        const payload = result.data?.unlinkContribution;
        if (payload?.success) {
          notifySuccess(
            t('pages.studentDetail.payments.toasts.contributionUnlinked'),
          );
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionUnlinkFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionUnlinkFailed'),
          [{ message: (error as Error).message, code: null }],
        );
        return false;
      }
    },
    [detachMutation, notifySuccess, notifyError, t],
  );

  const addContribution = useCallback(
    async ({
      student,
      agreement,
      date,
      amountForeign,
    }: IAddContributionArgs) => {
      try {
        const existing: ContributionInput[] = (
          agreement.contributions?.edges || []
        )
          .map((edge) => edge?.node)
          .filter((node): node is NonNullable<typeof node> => Boolean(node))
          .map((node) => ({
            date: node.date,
            sum: Number(node.amountForeign ?? 0),
          }));
        const nextContributions: ContributionInput[] = [
          ...existing,
          { date, sum: amountForeign },
        ];
        const u = student.user;
        const result = await editAgreementMutation({
          variables: {
            agreementId: agreement.id,
            updateStudentInput: {
              student: {
                firstName: u?.firstName || '',
                lastName: u?.lastName || '',
                middleName: u?.middleName || '',
                email: u?.email || '',
                phone: u?.phone || '',
              },
              agreement: {
                agreementTitle: agreement.agreementTitle,
                currency: agreement.currency || '',
                group_id: agreement.group.id,
                paymentByCompany: agreement.paymentByCompany ?? false,
                partialAttendance: agreement.partialAttendance ?? false,
                startAgreementBudget:
                  typeof agreement.budgetAmount === 'number'
                    ? agreement.budgetAmount
                    : null,
                countryId: agreement.country?.id || null,
                promocode: agreement.promocode || null,
                vat: agreement.vat ?? false,
                resident: agreement.resident ?? true,
                terminated: agreement.terminated ?? false,
                date: agreement.date || null,
                comment: agreement.comment || null,
                terminatedDate: agreement.terminatedDate || null,
              },
              company: agreement.company?.id
                ? { companyId: agreement.company.id }
                : null,
              contributions: nextContributions,
            },
          },
        });
        const payload = result.data?.updateStudentWithAgreement;
        if (payload?.success) {
          notifySuccess(
            t('pages.studentDetail.payments.toasts.contributionAdded'),
          );
          return true;
        }
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionAddFailed'),
          payload?.customErrors,
        );
        return false;
      } catch (error) {
        notifyError(
          t('pages.studentDetail.payments.toasts.contributionAddFailed'),
          [{ message: (error as Error).message, code: null }],
        );
        return false;
      }
    },
    [editAgreementMutation, notifySuccess, notifyError, t],
  );

  return {
    createPayment,
    updatePayment,
    deletePayment,
    linkContribution,
    unlinkContribution,
    addContribution,
    loading:
      createState.loading ||
      updateState.loading ||
      deleteState.loading ||
      attachState.loading ||
      detachState.loading ||
      editAgreementState.loading,
  };
};
